import { useRef, useMemo, useCallback } from "react";
import type { Channel, Program } from "@hybrid-iptv/types";
import { useEpgSettings, GUIDE_REACH_HOURS } from "./settingsStore.js";
import { EpgGrid } from "./EpgGrid.js";
import { ChannelList } from "./ChannelList.js";
import { EpgScrollRail } from "./EpgScrollRail.js";
import { PreviewInfo } from "./PreviewInfo.js";
import { useGuideFocus } from "./guideFocus.js";
import { useGuideTouchScroll } from "./touchScroll.js";
import type { ChannelProgramIndex } from "./hooks.js";

interface TraditionalLayoutProps {
  /** Channels shown in the guide (category-filtered). */
  channels: Channel[];
  /** The full channel list — used to resolve the selected/playing channel even
   *  when it belongs to a different category than the one being browsed. */
  allChannels: Channel[];
  selectedChannelId: string | null;
  programIndex: ChannelProgramIndex | null;
  onChannelSelect: (channel: Channel) => void;
  onProgramSelect: (program: Program, channel: Channel) => void;
  /** Long-press / hold-OK / right-click on a programme. */
  onProgramContext?: (program: Program, channel: Channel, point: { x: number; y: number }) => void;
  /** Long-press / hold-OK / right-click on a channel row. */
  onChannelContext?: (channel: Channel, point: { x: number; y: number }) => void;
}

export function TraditionalLayout({
  channels,
  allChannels,
  selectedChannelId,
  programIndex,
  onChannelSelect,
  onProgramSelect,
  onProgramContext,
  onChannelContext,
}: TraditionalLayoutProps) {
  const timeWindow = useEpgSettings((s) => s.timeWindow);
  const sharedScrollRef = useRef<HTMLDivElement>(null);

  // Forward mouse wheel + touch scroll from the guide containers to the shared
  // scroll rail. The rail is the only element with overflow-y:auto; the channel
  // list and grid use overflow:hidden (virtualized) so wheel events there are
  // swallowed unless we forward them.
  const forwardWheel = useCallback((e: React.WheelEvent) => {
    if (sharedScrollRef.current) {
      sharedScrollRef.current.scrollTop += e.deltaY;
    }
  }, []);
  const touchScroll = useGuideTouchScroll(sharedScrollRef);

  // Resolve from the FULL list: the channel that's playing may sit in another
  // category than the one being browsed, and the info panel must still show it.
  const selectedChannel = useMemo(
    () => (selectedChannelId ? allChannels.find((c) => c.id === selectedChannelId) ?? null : null),
    [selectedChannelId, allChannels],
  );

  // Roving D-pad focus across the rail and the program grid (TV-first).
  const initialRow = useMemo(() => {
    if (!selectedChannelId) return 0;
    const idx = channels.findIndex((c) => c.id === selectedChannelId);
    return idx >= 0 ? idx : 0;
  }, [channels, selectedChannelId]);
  // The arrows only move the focus ring; OK on a row or block tunes it (see
  // the click handlers below / EpgProgramBlock's key handling).
  const { focus, focusAt, handleKeyDown } = useGuideFocus({
    channels,
    programIndex,
    timeWindowHours: GUIDE_REACH_HOURS,
    sharedScrollRef,
    initialRow,
  });

  return (
    <div className="epg-traditional-layout">
      <div className="epg-traditional-preview">
        <div className="epg-traditional-preview-placeholder">
          <span className="epg-traditional-preview-text">
            {selectedChannelId ? "▶ Playing in this preview" : "Select a channel to preview"}
          </span>
        </div>
        <div className="epg-traditional-preview-info">
          <PreviewInfo channel={selectedChannel} programIndex={programIndex} />
        </div>
      </div>
      <div className="epg-traditional-main" data-guide="" onKeyDown={handleKeyDown}>
        <EpgScrollRail rowCount={channels.length} scrollRef={sharedScrollRef} />
        <div className="epg-traditional-channels" onWheel={forwardWheel} {...touchScroll}>
          <ChannelList
            channels={channels}
            selectedChannelId={selectedChannelId}
            programIndex={programIndex}
            onSelect={onChannelSelect}
            onContext={onChannelContext}
            virtualScrollRef={sharedScrollRef}
            focusRow={focus.row}
            focusCol={focus.col}
            onFocusAt={focusAt}
          />
        </div>
        <div className="epg-traditional-grid" onWheel={forwardWheel} {...touchScroll}>
          <EpgGrid
            channels={channels}
            programIndex={programIndex}
            timeWindowHours={GUIDE_REACH_HOURS}
            hoursOnScreen={timeWindow}
            selectedChannelId={selectedChannelId}
            sharedScrollRef={sharedScrollRef}
            onProgramSelect={onProgramSelect}
            onProgramContext={onProgramContext}
            onChannelSelect={onChannelSelect}
            onChannelContext={onChannelContext}
            focusRow={focus.row}
            focusCol={focus.col}
            onFocusAt={focusAt}
          />
        </div>
      </div>
    </div>
  );
}
